import { KeyboardArrowRight } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t } from 'Components/ui';
import type { CourseRow } from 'Components/CoursesTable';

interface CoursesAccordionProps {
  title: string;
  rows: CourseRow[];
  /** Texto cuando la herencia está vacía. */
  emptyLabel: string;
  /** Encabezados de la lista interna. */
  labels: { name: string; terminal: string; duration: string };
}

// `details`/`summary` nativos: abrir y cerrar, teclado y semántica ya vienen
// resueltos por el navegador. El Accordion de MUI que había aquí traía un
// Paper, un Table y un estado propio para hacer lo mismo.
const Box = styled.details({
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.lg,
  overflow: 'hidden',
  boxShadow: t.shadow.card,
  '&[open] .accordion-chevron': { transform: 'rotate(90deg)' },
});

const Summary = styled.summary({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 20px',
  cursor: 'pointer',
  listStyle: 'none',
  userSelect: 'none',
  '&::-webkit-details-marker': { display: 'none' },
  '&:hover': { background: t.color.rowHoverBg },
  '& .MuiSvgIcon-root': { fontSize: 20, color: t.color.muted },
});

const Chevron = styled.span({
  display: 'inline-flex',
  transition: 'transform 0.15s ease',
});

const SummaryText = styled.span({
  fontFamily: t.font,
  fontSize: 14,
  fontWeight: 600,
  color: t.color.title,
});

const Count = styled.span({
  padding: '3px 9px',
  borderRadius: 999,
  background: t.color.countBg,
  color: t.color.countText,
  fontFamily: t.font,
  fontSize: 12,
  fontWeight: 600,
});

const GRID = '2.4fr 1.3fr 0.9fr';

const HeadRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 16,
  padding: '10px 20px',
  background: t.color.tableHeadBg,
  borderTop: `1px solid ${t.color.rowBorder}`,
});

const HeadCell = styled.span({
  fontFamily: t.font,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '0.6px',
  color: t.color.tableHeadText,
});

const BodyRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 16,
  padding: '11px 20px',
  borderTop: `1px solid ${t.color.rowBorder}`,
});

const Name = styled.div({
  fontFamily: t.font,
  fontSize: 13.5,
  fontWeight: 600,
  color: t.color.title,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Cell = styled.div({
  fontFamily: t.font,
  fontSize: 13,
  color: t.color.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Empty = styled.div({
  padding: '18px 20px',
  borderTop: `1px solid ${t.color.rowBorder}`,
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.muted,
});

/**
 * Molécula: lista plegable de los cursos que el empleado ya arrastra de un
 * nivel superior (empresa, departamento).
 *
 * Es informativa y por eso no tiene casillas: esos cursos no se definen aquí,
 * pero saber que ya están evita volver a asignarlos. Arranca cerrada porque lo
 * que se viene a hacer a la pantalla está más abajo.
 */
const CoursesAccordion = ({ title, rows, emptyLabel, labels }: CoursesAccordionProps) => (
  <Box>
    <Summary>
      <Chevron className="accordion-chevron">
        <KeyboardArrowRight />
      </Chevron>
      <SummaryText>{title}</SummaryText>
      <Count>{rows.length}</Count>
    </Summary>

    {rows.length === 0 ? (
      <Empty>{emptyLabel}</Empty>
    ) : (
      <>
        <HeadRow>
          <HeadCell>{labels.name}</HeadCell>
          <HeadCell>{labels.terminal}</HeadCell>
          <HeadCell>{labels.duration}</HeadCell>
        </HeadRow>
        {rows.map(row => (
          <BodyRow key={row.id}>
            <Name>{row.name}</Name>
            <Cell>{row.terminalNames?.join(', ') || '—'}</Cell>
            <Cell>{row.duration}</Cell>
          </BodyRow>
        ))}
      </>
    )}
  </Box>
);

export default CoursesAccordion;
